import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { toast } from 'sonner'

type VoteType = 'working' | 'not_working' | 'needs_update'

interface Votes {
  working: number
  not_working: number
  needs_update: number
}

export const useVotes = (encryptorId: string) => {
  const { isAuthenticated } = useAuth()
  const [votes, setVotes] = useState<Votes>({ working: 0, not_working: 0, needs_update: 0 })
  const [loading, setLoading] = useState(false)

  const fetchVotes = async () => {
    try {
      const res = await fetch(`/api/votes/get?encryptorId=${encodeURIComponent(encryptorId)}`)
      if (res.ok) {
        const data = await res.json()
        setVotes(data.votes)
      }
    } catch {
      // Fall back to localStorage
      const record = localStorage.getItem(`votes_${encryptorId}`)
      if (record) {
        const parsed = JSON.parse(record)
        setVotes(parsed.votes || { working: 0, not_working: 0, needs_update: 0 })
      }
    }
  }

  useEffect(() => {
    fetchVotes()
  }, [encryptorId])

  const castVote = async (voteType: VoteType) => {
    if (!isAuthenticated) {
      toast.error('You must be logged in to vote.')
      return
    }

    if (loading) return
    setLoading(true)

    try {
      const res = await fetch('/api/votes/cast', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ encryptorId, voteType }),
      })
      const data = await res.json()

      if (res.ok) {
        setVotes(data.votes)
        toast.success('Vote recorded!')
      } else {
        toast.error(data.error || 'Failed to vote')
      }
    } catch {
      toast.error('Network error. Vote saved locally.')
      // Fallback: save to localStorage
      const record = localStorage.getItem(`votes_${encryptorId}`)
      const parsed = record ? JSON.parse(record) : { votes: { working: 0, not_working: 0, needs_update: 0 }, lastVoted: 0 }
      parsed.votes[voteType]++
      parsed.lastVoted = Date.now()
      localStorage.setItem(`votes_${encryptorId}`, JSON.stringify(parsed))
      setVotes(parsed.votes)
    } finally {
      setLoading(false)
    }
  }

  return { votes, castVote, loading }
}
